import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/utils';
import { DIALOG_OVERLAY, DIALOG_CONTENT } from '../../shared/styles/animations';
import { OVERLAY_ICON_BUTTON } from '../../shared/styles/overlay-controls';
import { SAFE_AREA_CENTRED_TOP } from '../../shared/styles/layout.constants';
import {
  ModalSurfaceContent,
  ModalSurfaceRoot,
  useModalSurfaceContentRef,
} from './modal-scroll-lock';

/**
 * A MODAL dialog installs `react-remove-scroll`, which swallows wheel events
 * over anything portaled out of it — every dropdown panel in the app. Publish
 * the modality so `PopoverContent` can bring its own lock and shard this
 * dialog's body back in. See `modal-scroll-lock`.
 */
const Dialog = ({
  modal = true,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => (
  <ModalSurfaceRoot modal={modal}>
    <DialogPrimitive.Root modal={modal} {...props} />
  </ModalSurfaceRoot>
);
Dialog.displayName = 'Dialog';

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80', DIALOG_OVERLAY, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    /**
     * `overlay` seats the close button on a scrim chip. Use it whenever the
     * dialog is full-bleed media (image / PDF / video preview) — the default
     * bare glyph disappears against a black or white image.
     */
    closeButtonVariant?: 'default' | 'overlay';
  }
>(({ className, children, hideCloseButton, closeButtonVariant = 'default', style, ...props }, ref) => {
  const { t } = useTranslation('common');
  const { contentRef, setContentRef } = useModalSurfaceContentRef(ref);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setContentRef}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg sm:rounded-lg',
          DIALOG_CONTENT,
          className
        )}
        // A dialog is `position: fixed`, so `top: 50%` is the middle of the
        // VIEWPORT — which on an edge-to-edge Android shell runs behind the
        // status bar and the navigation bar. Centring a tall dialog there puts
        // its footer, i.e. its actions, under the navigation bar. Re-centre on
        // the safe region instead; this resolves to exactly 50% wherever both
        // insets are 0, so nothing moves on the web or on desktop.
        style={{ top: SAFE_AREA_CENTRED_TOP, ...style }}
        {...props}
      >
        <ModalSurfaceContent contentRef={contentRef}>{children}</ModalSurfaceContent>
        {!hideCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              'absolute right-4 top-4',
              closeButtonVariant === 'overlay'
                ? OVERLAY_ICON_BUTTON
                : 'rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{t('app.close')}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-start',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
